import { Modal, Button, Row, Col, Accordion, Dropdown } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { Reservation } from "./BookingRequests";
import { TooltipCustom } from "@services/ui-components";
import moment from "moment/moment";
import {
    GetReservationsQueryVariables,
    useReservationChangeStatusMutation,
} from "@me-team/host/meBooking/restaurantBooking/graphql/restaurantBooking.hooks";
import { ReservationStatus } from "@me-team/host/meBooking/graphql/types";
import { apolloClientMeBooking } from "@me-team/host/src/apollo/apollo-client";
import { useTranslation } from "react-i18next";
import { GetReservationsQuery } from "@me-team/host/meBooking/restaurantBooking/graphql/restaurantBooking.hooks";
import { QueryResult } from "@apollo/client";
import TableIcon from "./svg/TableIcon";
import { formattedPhone } from "../../utils/formattedPhone";

interface BookingModalProps {
    show: boolean;
    handleClose: () => void;
    reservation: Reservation | null;
    refetchReservations:
        | QueryResult<GetReservationsQuery, GetReservationsQueryVariables>["refetch"]
        | (() => Promise<void>);
}

const BookingModal: React.FC<BookingModalProps> = ({ handleClose, show, reservation, refetchReservations }) => {
    const reservationDayIndex = moment(reservation?.reservationAt).day();
    const todaySchedule = reservation?.branch?.generalWorkSchedules?.find(
        (schedule) => schedule.day?.number === reservationDayIndex
    );

    const { t } = useTranslation();
    let closingTime = "";
    if (todaySchedule?.period) {
        const parts = todaySchedule.period.split(/-|\s*—\s*/);
        if (parts) {
            closingTime = moment(parts[1].trim(), "HH:mm").format("h:mm A");
        }
    }
    type StatusKey = "IN_PROGRESS" | "CONFIRMED" | "NO_CONNECTION" | "REJECTED" | "PENDING" | "WAITING_FOR_OPENING";
    interface StatusOption {
        label: string;
        color: string;
    }

    const statusMap: Record<StatusKey, StatusOption> = {
        PENDING: { label: t("Pending"), color: "bg-light-blue" },
        WAITING_FOR_OPENING: { label: t("Waiting for opening"), color: "bg-grey" },
        IN_PROGRESS: { label: t("In progress"), color: "bg-warning" },
        CONFIRMED: { label: t("Confirmed"), color: "bg-green" },
        REJECTED: { label: t("Rejected"), color: "bg-danger" },
        NO_CONNECTION: { label: t("No Connection"), color: "bg-pink" },
    };

    const [status, setStatus] = useState<StatusKey | null>(null);
    useEffect(() => {
        if (reservation?.status) {
            setStatus(reservation.status as StatusKey);
        }
    }, [reservation?.status]);

    const [comment, setComment] = useState<string | null>(reservation?.moderatorComment ?? null);
    const [changeStatus, { loading }] = useReservationChangeStatusMutation({
        client: apolloClientMeBooking,
    });
    const handleSubmit = async () => {
        try {
            const variables: { uuid: string; status: ReservationStatus; comment: string | null } = {
                uuid: reservation.uuid as string,
                status: status as ReservationStatus,
                comment: comment ?? null,
            };

            await changeStatus({ variables });
            await refetchReservations();
            setComment("");
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };
    if (!reservation) return null;
    return (
        <Modal scrollable show={show} centered onHide={handleClose}>
            <Modal.Header className="border-0" closeButton>
                <div>
                    <Modal.Title className="fs-24 d-flex align-items-center">
                        {reservation.status === "IN_PROGRESS" && (
                            <span className="d-flex align-items-center">
                                {t("New application")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-warning-dark text-warning-dark bg-warning-light">
                                    {t("In progress")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "CONFIRMED" && (
                            <span>
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-green text-green bg-additional-green">
                                    {t("Confirmed")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "NO_CONNECTION" && (
                            <span>
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-pink text-pink bg-additional-pink">
                                    {t("No Connection")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "REJECTED" && (
                            <span>
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-danger text-danger bg-danger-light">
                                    {t("Rejected")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "CANCELLED_BY_USER" && (
                            <span>
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-dark text-darh bg-additional-grey">
                                    {t("Canceled")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "PENDING" && (
                            <span>
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-dark text-darh bg-light-blue">
                                    {t("Pending")}
                                </span>
                            </span>
                        )}
                        {reservation.status === "WAITING_FOR_OPENING" && (
                            <span className="d-flex align-items-center">
                                {t("Application details")}
                                <span className="ms-2 fs-7 d-inline-block px-2 py-1 rounded border border-dark text-dark bg-additional-light-blue">
                                    {t("Waiting for opening")}
                                </span>
                            </span>
                        )}
                    </Modal.Title>
                    <span className="fs-7 text-grey">
                        {t("Issued")}: {moment(reservation?.reservationAt).format("DD.MM.YYYY")},{" "}
                        {moment(reservation?.reservationAt).format("h:mm")} • ID: {reservation.id}
                    </span>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Accordion className="border rounded mb-3 accordion-booking" defaultActiveKey={["0"]} alwaysOpen>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header className="border-0">{t("Contacts of the restaurant")}</Accordion.Header>
                        <Accordion.Body className="pb-4 text-dark">
                            <div className="d-flex mb-3 align-items-center">
                                <span className="flex-shrink-0">
                                    <TableIcon />
                                </span>
                                {reservation.branch ? (
                                    <a
                                        href={`${process.env.REACT_APP_ME_BOOKING}/${reservation.branch.countryProxy.slug}/${reservation.branch.city.slug}/${reservation.branch.branchDetails.slug}`}
                                        className="mb-0 fw-bold"
                                        target="_blank"
                                    >
                                        {reservation.branch.name}
                                    </a>
                                ) : (
                                    <span className="mb-0 fw-bold">{reservation.customPlace?.name}</span>
                                )}
                            </div>
                            {reservation.branch && (
                                <div className="d-flex mb-3">
                                    <i className="bi bi-telephone me-2"></i>
                                    <p className="mb-0">
                                        {reservation.branch.phones.map((phone) => (
                                            <span key={phone.phone}>{formattedPhone(phone.phone)}</span>
                                        ))}
                                    </p>
                                </div>
                            )}
                            <div className="d-flex mb-3">
                                <i className="bi bi-geo-alt me-2"></i>
                                <p className="mb-0">
                                    {reservation.branch?.branchDetails.formatedAddress ??
                                        reservation.customPlace?.location}
                                </p>
                            </div>

                            {reservation.branch && (
                                <div className="d-flex">
                                    <i className="bi bi-clock-history me-2"></i>
                                    <p className="mb-0">
                                        {moment(reservation?.reservationAt).format("dddd, D MMMM")} —{" "}
                                        {t("the restaurant works until")} <b>{closingTime}</b>
                                    </p>
                                    <TooltipCustom
                                        placement="bottom"
                                        text={
                                            <table>
                                                <tbody>
                                                    {reservation.branch.generalWorkSchedules.map((schedule) => (
                                                        <tr key={schedule.id}>
                                                            <td>{schedule.day.name}&nbsp;</td>
                                                            <td>
                                                                <b>{schedule.period}</b>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        }
                                    >
                                        <i className="bi bi-info-circle ms-3"></i>
                                    </TooltipCustom>
                                </div>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                <Accordion className="border rounded accordion-booking mb-3" defaultActiveKey={["0"]} alwaysOpen>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header className="border-0">{t("Booking information")}</Accordion.Header>
                        <Accordion.Body className="pb-4 text-dark">
                            <div className="d-flex mb-3">
                                <i className="bi bi-person me-2"></i>
                                <p className="mb-0 fw-bold">
                                    {reservation.client.name} {reservation.client.surname}
                                </p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-telephone me-2"></i>
                                <p className="mb-0">{formattedPhone(reservation.client.phone)}</p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-envelope me-2"></i>
                                <p className="mb-0">{reservation.client.email}</p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-calendar4-event me-2"></i>
                                <p className="mb-0">
                                    {moment(reservation?.reservationAt).format("dddd, D MMMM, YYYY")}
                                </p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-clock me-2"></i>
                                <p className="mb-0">
                                    {moment(reservation?.reservationAt).format("h:mm A")}
                                    {reservation.flexibility != "NONE" && (
                                        <span className="ms-2 d-inline-block px-2 rounded bg-additional-violet text-primary">
                                            ± {reservation.flexibility.slice(5, 6)}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-people me-2"></i>
                                <p className="mb-0">
                                    {reservation.guestsCount} {reservation.guestsCount === 1 ? t("guest") : t("guests")}
                                </p>
                            </div>
                            <div className="d-flex">
                                <i className="bi bi-card-text me-2"></i>
                                <p className="mb-0">{reservation.comment ? reservation.comment : "–"}</p>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                <div className="border rounded p-3">
                    <p className="fw-bold fs-20">{t("Application status")}</p>
                    {status != "CANCELLED_BY_USER" ? (
                        <Dropdown className="mb-4 status-dd">
                            <Dropdown.Toggle
                                className="w-100 rounded-3 bg-white border-secondary text-dark d-flex justify-content-between"
                                id="dropdown-basic"
                            >
                                <span className="d-flex align-items-center fs-16">
                                    <span
                                        className={`d-inline-block me-2 rounded-1 status-dd-icon ${statusMap[status]?.color}`}
                                    ></span>
                                    {statusMap[status]?.label}
                                </span>
                                <i className="bi bi-chevron-down"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="w-100">
                                {Object.entries(statusMap).map(([key, option]) => (
                                    <Dropdown.Item
                                        key={key}
                                        className="py-2"
                                        onClick={() => setStatus(key as StatusKey)}
                                    >
                                        <span className="d-flex align-items-center">
                                            <span
                                                className={`d-inline-block me-2 rounded-1 status-dd-icon ${option.color}`}
                                            ></span>
                                            {option.label}
                                        </span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        t("CanceledByUser")
                    )}
                    {status != "IN_PROGRESS" && status != "CANCELLED_BY_USER" && status != "PENDING" && (
                        <div className="form-floating">
                            <textarea
                                className="form-control rounded-3"
                                placeholder={t("Comment")}
                                id="floatingTextarea2"
                                style={{ height: "110px" }}
                                value={comment ?? ""}
                                onChange={(e) => setComment(e.target.value)}
                            />
                            <label htmlFor="floatingTextarea2">{t("Comment")}</label>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Row className="w-100 gx-3">
                    <Col sm={6} className="ps-lg-0 mb-3 mb-lg-0">
                        <Button className="w-100" variant="outline-primary" onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                    </Col>
                    <Col sm={6} className="pe-lg-0">
                        <Button
                            className="w-100"
                            variant="primary"
                            disabled={status === reservation.status || loading}
                            onClick={async () => {
                                await handleSubmit();
                            }}
                        >
                            {t("To process")}
                        </Button>
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    );
};

export default BookingModal;
